import { inject, Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import {
  AuthService,
  UserProfileDto,
  AuthServiceV2,
  ApiResponseOfUserProfileDtoV2,
} from 'api-client';
import { Observable, of, tap, catchError, map, from, switchMap } from 'rxjs';
import { LogService } from 'shared';

import { PushNotificationService } from '../../../services/push-notification.service';
import { BiometricService } from '../services/biometric.service';
import { CredentialVaultService } from '../services/credential-vault.service';

const USER_PROFILE_KEY = 'userProfile';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly authService = inject(AuthService);
  private readonly authServiceV2 = inject(AuthServiceV2);
  private readonly tokenStorage = inject(CredentialVaultService);
  private readonly biometricService = inject(BiometricService);
  private readonly pushService = inject(PushNotificationService);
  private readonly logger = inject(LogService);

  private readonly _user = signal<UserProfileDto | null>(null);
  private readonly _isAuthenticated = signal(false);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _biometricAvailable = signal(false);
  private readonly _biometricEnabled = signal(false);
  private readonly _pinConfigured = signal(false);

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly biometricAvailable = this._biometricAvailable.asReadonly();
  readonly biometricEnabled = this._biometricEnabled.asReadonly();
  readonly pinConfigured = this._pinConfigured.asReadonly();

  constructor() {
    void this.initializeStore();
  }

  private async initializeStore(): Promise<void> {
    await this.restoreAuthStateFromStorage();
    await this.restoreUserProfileFromStorage();
    await this.checkBiometricStatus();
    await this.checkPinStatus();
  }

  private async checkBiometricStatus(): Promise<void> {
    const status = await this.biometricService.checkAvailability();
    this._biometricAvailable.set(status.isAvailable);

    if (status.isAvailable) {
      const enabled = await this.biometricService.isBiometricEnabled();
      this._biometricEnabled.set(enabled);
    }
  }

  private async storeUserProfile(profile: UserProfileDto | null): Promise<void> {
    if (profile) {
      await Preferences.set({
        key: USER_PROFILE_KEY,
        value: JSON.stringify(profile),
      });
    } else {
      await Preferences.remove({ key: USER_PROFILE_KEY });
    }
  }

  private async restoreUserProfileFromStorage(): Promise<void> {
    const { value: stored } = await Preferences.get({ key: USER_PROFILE_KEY });

    if (stored) {
      try {
        const profile = JSON.parse(stored) as UserProfileDto;
        this._user.set(profile);
        this._isAuthenticated.set(true);
      } catch {
        this.logger.error(
          'Failed to parse stored user profile',
          'AuthStore.restoreUserProfileFromStorage',
        );
        await Preferences.remove({ key: USER_PROFILE_KEY });
      }
    }
  }

  restoreUserProfileFromApi(): Observable<boolean> {
    return from(this.tokenStorage.getToken()).pipe(
      switchMap((token) => {
        if (!token?.accessToken) {
          this._isAuthenticated.set(false);
          this._user.set(null);
          return of(false);
        }

        this._isLoading.set(true);
        return this.authServiceV2.apiV2AuthProfileGet().pipe(
          tap((response: ApiResponseOfUserProfileDtoV2 | null) => {
            const user = (response?.data as UserProfileDto) ?? null;
            if (user) {
              this._user.set(user);
              this._isAuthenticated.set(true);
              void this.storeUserProfile(user);
            } else {
              this._isAuthenticated.set(false);
              this._user.set(null);
              void this.storeUserProfile(null);
              void this.tokenStorage.clearTokens();
            }
            this._isLoading.set(false);
          }),
          map((response) => !!response?.data),
          catchError(() => {
            this._isAuthenticated.set(false);
            this._user.set(null);
            this._isLoading.set(false);
            return of(false);
          }),
        );
      }),
    );
  }

  async restoreAuthStateFromStorage(): Promise<void> {
    const token = await this.tokenStorage.getToken();
    if (token?.accessToken) {
      const isExpired = await this.tokenStorage.isAccessTokenExpired();
      this._isAuthenticated.set(!isExpired);
    } else {
      this._isAuthenticated.set(false);
      this._user.set(null);
    }
  }

  setUser(user: UserProfileDto | null): void {
    this._user.set(user);
  }

  setUserPhotoUrl(photoUrl: string): void {
    const user = this._user();
    if (user) {
      this._user.set({ ...user, photoUrl });
    }
  }

  setUserPhoto(photoUrl: string, photoVersion?: number): void {
    const user = this._user();
    if (user) {
      this._user.set({ ...user, photoUrl, photoVersion });
      void this.storeUserProfile({ ...user, photoUrl, photoVersion });
    }
  }

  setAuthenticated(auth: boolean): void {
    this._isAuthenticated.set(auth);
  }

  async logout(): Promise<void> {
    await this.pushService.unregister();
    await this.tokenStorage.clearAll();
    await Preferences.remove({ key: USER_PROFILE_KEY });
    this._isAuthenticated.set(false);
    this._user.set(null);
    this._pinConfigured.set(false);
  }

  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }

  setError(error: string | null): void {
    this._error.set(error);
  }

  clearError(): void {
    this._error.set(null);
  }

  async enableBiometric(): Promise<void> {
    await this.biometricService.enableBiometric();
    this._biometricEnabled.set(true);
  }

  async disableBiometric(): Promise<void> {
    await this.biometricService.disableBiometric();
    this._biometricEnabled.set(false);
  }

  async refreshBiometricStatus(): Promise<void> {
    await this.checkBiometricStatus();
  }

  async refreshPinStatus(): Promise<void> {
    await this.checkPinStatus();
  }

  private async checkPinStatus(): Promise<void> {
    const configured = await this.tokenStorage.isPinConfigured();
    this._pinConfigured.set(configured);
  }

  clear(): void {
    this._user.set(null);
    this._isAuthenticated.set(false);
    this._error.set(null);
  }
}
